import type { KeywordSummary, Status } from "../../lib/api.js";

/**
 * Compact table of keyword rows. Stage dots show pipeline progress at a glance:
 *   [SERP] [Brief] [Article] [Markdown] — green if present, grey if not.
 */
export function KeywordTable({
  keywords,
  loading,
  selectedId,
  onSelect,
}: {
  keywords: KeywordSummary[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (k: KeywordSummary) => void;
}) {
  if (loading) {
    return (
      <div className="card overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse border-b border-line bg-canvas last:border-b-0" />
        ))}
      </div>
    );
  }
  if (keywords.length === 0) {
    return (
      <div className="card p-12 text-center text-muted">
        No keywords match the current filters.
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="grid grid-cols-[1fr_120px_140px_120px_160px_120px] gap-4 border-b border-line bg-canvas px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted">
        <div>Keyword</div>
        <div>Language</div>
        <div>Status</div>
        <div>Priority</div>
        <div title="✓ = done. Stages: Research · Brief · Article · Images · Video · Published · Distributed">Pipeline</div>
        <div>Updated</div>
      </div>
      <div>
        {keywords.map((k) => (
          <button
            key={k.id}
            onClick={() => onSelect(k)}
            className={`grid w-full grid-cols-[1fr_120px_140px_120px_160px_120px] gap-4 border-b border-line px-4 py-3 text-left text-sm transition last:border-b-0 hover:bg-canvas ${
              selectedId === k.id ? "bg-brand/5" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="truncate font-medium" dir={k.language === "he" ? "rtl" : "ltr"}>
                {k.keyword}
                {k.pillarPage && <span className="ml-2 text-xs text-brand">●</span>}
              </div>
              <div className="truncate text-xs text-muted">
                {k.id}
                {k.searchVolume != null && (
                  <span className="ml-2 text-accent" title="Stored monthly search volume (keyword_metrics)">
                    · {k.searchVolume.toLocaleString()}/mo
                  </span>
                )}
              </div>
            </div>
            <div className="text-xs uppercase tracking-wide text-muted">{k.language}</div>
            <div className="flex flex-wrap items-center gap-1">
              <StatusPill status={k.status} />
              {/* Social at a glance: posts live out of all posts for this article.
                  Green once something went live, blue while Postiz holds posts
                  and none are out yet, grey when nothing has left. */}
              {k.social && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    k.social.posted > 0
                      ? "bg-emerald-100 text-emerald-800"
                      : k.social.scheduled > 0
                        ? "bg-sky-100 text-sky-800"
                        : "bg-slate-100 text-slate-600"
                  }`}
                  title={`סושיאל: ${k.social.posted} פורסמו · ${k.social.scheduled} מתוזמנים בפוסטיז · ${k.social.total} בסך הכל. הפירוט בטאב Social.`}
                >
                  social {k.social.posted}/{k.social.total}
                </span>
              )}
              {k.unpublishedChanges && (
                <span
                  className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                  title="The pipeline version is newer than what's live — re-publish to push your changes."
                >
                  unpublished
                </span>
              )}
              {k.coveredByUrl && (
                <a
                  href={k.coveredByUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-200"
                  title={`A live page already serves this keyword — producing an article would compete with it.\n${k.coveredByUrl}`}
                >
                  covered ↗
                </a>
              )}
              {/*
                A live page targets this exact phrase. Distinct from the "covered"
                pill above, which comes from the cluster's page_url: this one is
                the duplicate detector, and it is here rather than only in the
                detail panel because the article we wrote twice was chosen from
                this list, where nothing said a word about it.
              */}
              {k.duplicateOf && (
                <a
                  href={k.duplicateOf.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    k.duplicateOf.decision === "block"
                      ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                  }`}
                  title={
                    (k.duplicateOf.decision === "block"
                      ? "A live BLOG ARTICLE already targets this keyword — producing is blocked unless you override it:\n"
                      : "A live page targets this keyword. Not a duplicate, but look before writing:\n") +
                    `${k.duplicateOf.title}\n${k.duplicateOf.url}`
                  }
                >
                  {k.duplicateOf.decision === "block" ? "already written ↗" : "has a page ↗"}
                </a>
              )}
            </div>
            <div className="text-xs text-muted">{k.priority}</div>
            <StageStrip stages={k.stages} />
            <div className="text-xs text-muted">{relativeTime(k.updatedAt)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const palette: Record<Status, string> = {
    backlog: "bg-line text-muted",
    researching: "bg-warn/10 text-warn",
    briefed: "bg-brand/10 text-brand",
    drafting: "bg-brand/10 text-brand",
    reviewing: "bg-brand/10 text-brand",
    published: "bg-accent/10 text-accent",
    distributed: "bg-accent/10 text-accent",
    archived: "bg-line text-muted",
    rejected: "bg-danger/10 text-danger",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${palette[status]}`}>
      {status}
    </span>
  );
}

/**
 * Per-action completion strip: a ✓ when the action is done, otherwise the
 * stage's initial (R·B·A·I·V·P·D) so it's obvious what's next. Hover for the
 * full label + state.
 */
function StageStrip({ stages }: { stages: { key: string; label: string; done: boolean }[] }) {
  return (
    <div className="flex items-center gap-0.5">
      {stages.map((s) => (
        <span
          key={s.key}
          title={`${s.label}: ${s.done ? "done ✓" : "not yet"}`}
          className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold leading-none ${
            s.done ? "bg-accent/15 text-accent" : "bg-line text-muted/60"
          }`}
        >
          {s.done ? "✓" : s.label.charAt(0)}
        </span>
      ))}
    </div>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}
